import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { DeckGroupForm } from '@/components/DeckGroupForm';
import { useDeckGroup, useUpdateDeckGroup } from '@/queries/deck-groups';
import { CircularLoader } from '@/components/CircularLoader';
import { BackgroundWaves } from '@/components/BackgroundWaves';
import type { UpdateDeckGroupRequest } from '@/types/deck-group';
import styles from '../quiz-page.module.scss';

export const Route = createFileRoute('/quizlets/deck-groups/$groupId/edit')({
  component: EditDeckGroup,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function EditDeckGroup() {
  const { groupId } = Route.useParams();
  const navigate = useNavigate();
  const { data: deckGroup, isLoading, error } = useDeckGroup(groupId);
  const updateDeckGroup = useUpdateDeckGroup();

  const handleSave = async (data: UpdateDeckGroupRequest) => {
    await updateDeckGroup.mutateAsync({ id: groupId, data });
    navigate({
      to: '/quizlets',
      search: { tab: 'grouped' },
    });
  };

  const handleCancel = () => {
    navigate({
      to: '/quizlets',
      search: { tab: 'grouped' },
    });
  };

  if (isLoading) {
    return (
      <div className={styles.quizPage}>
        <BackgroundWaves />
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <CircularLoader />
        </div>
      </div>
    );
  }

  if (error || !deckGroup) {
    return (
      <div className={styles.quizPage}>
        <BackgroundWaves />
        <div style={{ textAlign: 'center', padding: '4rem', color: 'var(--error-text)' }}>
          {error?.message || 'Deck group not found'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.quizPage}>
      <BackgroundWaves />

      <div className={styles.quizPage__breadcrumb}>
        <nav className={styles.quizPage__nav}>
          <a
            href="/quizlets?tab=grouped"
            className={styles.quizPage__link}
            onClick={(e) => {
              e.preventDefault();
              navigate({
                to: '/quizlets',
                search: { tab: 'grouped' },
              });
            }}
          >
            Grouped Decks
          </a>
          <span className={styles.quizPage__separator}>›</span>
          <span className={styles.quizPage__current}>Edit {deckGroup.name}</span>
        </nav>
      </div>

      <DeckGroupForm
        initialData={deckGroup}
        onSave={handleSave}
        onCancel={handleCancel}
        isLoading={updateDeckGroup.isPending}
        error={updateDeckGroup.error?.message}
      />
    </div>
  );
}
